export interface UserPermissions {
  inAppMessages: boolean;
  comments: boolean;
  commentSettings: boolean;
  taLocals: boolean;
  tidePoints: boolean;
  analytics: boolean;
  userManagement: boolean;
  contentModeration: boolean;
  volunteerEvents: boolean;
  trailObservations: boolean;
  isSuperAdmin: boolean;
}

export interface UserClaim {
  type: string;
  value: string;
}

export interface UserInfo {
  isAuthenticated: boolean;
  name?: string;
  email?: string;
  claims?: UserClaim[];
}

const ADMIN_GROUPS = {
  InAppMessagesManagers: 'TrailApp-Admin-Messages',
  CommentModerators: 'TrailApp-Admin-Comments',
  CommentSettingsManagers: 'TrailApp-Admin-CommentSettings',
  TALocalsManagers: 'TrailApp-Admin-TALocals',
  TidePointsManagers: 'TrailApp-Admin-TidePoints',
  AnalyticsViewers: 'TrailApp-Admin-Analytics',
  UserManagers: 'TrailApp-Admin-Users',
  ContentModerators: 'TrailApp-Admin-ContentModeration',
  VolunteerEventManagers: 'TrailApp-Admin-VolunteerEvents',
  TrailObservationViewers: 'TrailApp-Admin-Observations',
  SuperAdmins: 'TrailApp-Admin-SuperAdmin'
};

export class PermissionsService {
  private static getUserGroups(userInfo: UserInfo): string[] {
    if (!userInfo.claims) return [];
    
    // Check for various role/group claim types from Azure AD
    return userInfo.claims
      .filter(claim => 
        claim.type === 'roles' || 
        claim.type === 'groups' ||
        claim.type === 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role' ||
        claim.type === 'http://schemas.microsoft.com/ws/2008/06/identity/claims/group'
      )
      .map(claim => claim.value);
  }

  public static getUserPermissions(userInfo: UserInfo): UserPermissions {
    const userGroups = this.getUserGroups(userInfo);
    
    const isSuperAdmin = userGroups.includes(ADMIN_GROUPS.SuperAdmins);
    
    return {
      inAppMessages: isSuperAdmin || userGroups.includes(ADMIN_GROUPS.InAppMessagesManagers),
      comments: isSuperAdmin || userGroups.includes(ADMIN_GROUPS.CommentModerators),
      commentSettings: isSuperAdmin || userGroups.includes(ADMIN_GROUPS.CommentSettingsManagers),
      taLocals: isSuperAdmin || userGroups.includes(ADMIN_GROUPS.TALocalsManagers),
      tidePoints: isSuperAdmin || userGroups.includes(ADMIN_GROUPS.TidePointsManagers),
      analytics: isSuperAdmin || userGroups.includes(ADMIN_GROUPS.AnalyticsViewers),
      userManagement: isSuperAdmin || userGroups.includes(ADMIN_GROUPS.UserManagers),
      contentModeration: isSuperAdmin || userGroups.includes(ADMIN_GROUPS.ContentModerators),
      volunteerEvents: isSuperAdmin || userGroups.includes(ADMIN_GROUPS.VolunteerEventManagers),
      trailObservations: isSuperAdmin || userGroups.includes(ADMIN_GROUPS.TrailObservationViewers),
      isSuperAdmin
    };
  }

  public static hasAnyPermission(userInfo: UserInfo): boolean {
    const permissions = this.getUserPermissions(userInfo);
    return Object.values(permissions).some(permission => permission === true);
  }
}